import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import type { PatientRoomPropertyUsageResponse } from '../../api/generated';
import { PatientRoomPropertyControllerApi } from '../../api/generated';
import { createApiConfiguration } from '../../api/apiConfig';
import { useAppDispatch } from '../../store/hooks';
import { showError } from '../../store/errorDialog/errorDialogSlice';
import { Modal } from '../UI';
import { Button } from '../UI/base/Button';
import styles from './DeleteRoomNeedModal.module.css';

interface DeleteRoomNeedModalProps {
  property: PatientRoomPropertyUsageResponse;
  onClose: () => void;
  onDeleted: () => void;
}

export const DeleteRoomNeedModal = ({ property, onClose, onDeleted }: DeleteRoomNeedModalProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [inUseError, setInUseError] = useState(false);

  const handleConfirm = async () => {
    try {
      const config = createApiConfiguration();
      const api = new PatientRoomPropertyControllerApi(config);
      await api.deletePatientRoomProperty({ propertyId: property.id });
      onDeleted();
    } catch (error) {
      console.error('Failed to delete room property:', error);
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 409 &&
        typeof error.response.data === 'string'
      ) {
        setInUseError(true);
        return;
      }
      dispatch(showError({ message: t('delete-room-need-error') }));
    }
  };

  return (
    <Modal
      title={t('delete-room-need-title')}
      onClose={onClose}
      width={480}
      footer={
        <div className={styles.footer}>
          {inUseError ? (
            <Button type="primary" onClick={onClose}>
              {t('delete-room-need-close')}
            </Button>
          ) : (
            <>
              <Button type="secondary" onClick={onClose}>
                {t('delete-room-cancel')}
              </Button>
              <Button type="primary" onClick={handleConfirm}>
                {t('delete-room-need-confirm')}
              </Button>
            </>
          )}
        </div>
      }
    >
      <p className={styles.message}>
        {inUseError
          ? t('delete-room-need-in-use')
          : t('delete-room-need-message', { name: property.name })}
      </p>
    </Modal>
  );
};
